<template>
  <div class="card-item card-item-sensor">
    <icon-sensor class="icon" v-ifield-b-tooltip="iconTooltipText" />
    <span class="text-wrapper" v-ifield-b-tooltip="tooltipOption">
      <span
        class="text value"
        v-bind:style="{ '--basis-chars': thresholdChars.value }"
      >
        <span
          class="text"
          v-bind:class="{
            'ellipsis': needsToTruncate.value,
            'both': needsToTruncateBoth,
          }"
        >
          {{ valueText }}
        </span>
        <span v-if="canShowUnit" class="text unit">
          {{ unit }}
        </span>
      </span>
      <span
        class="text item-name"
        v-bind:style="{ '--basis-chars': thresholdChars.name }"
      >
        <span
          class="text"
          v-bind:class="{
            'ellipsis': needsToTruncate.name,
            'both': needsToTruncateBoth,
          }"
        >
          {{ sensorItemName }}
        </span>
      </span>
    </span>

    <!-- 幅計測用隠し要素 -->
    <span ref="value" class="hidden text value">
      <span class="text">{{ valueText }}</span>
      <span v-if="canShowUnit" class="text unit">{{ unit }}</span>
    </span>
    <span ref="name" class="hidden text item-name">
      <span class="text">{{ sensorItemName }}</span>
    </span>
  </div>
</template>

<script>
import { toRefs } from 'vue'
import IconSensor from '@/assets/ifield/ic_sensor.svg?component'
import VARIABLES from '@/assets/scss/variables.module.scss'
import { useUserStatusAdditionalItemSensorData } from '@/composables/user/additionalItem'

export default {
  name: 'AppCardMemberItemSensorData',
  components: {
    IconSensor,
  },
  inheritAttrs: false,
  props: {
    /**
     * センサー測定項目名
     */
    sensorItemName: {
      type: String,
      default: null,
    },
    /**
     * センサー値
     */
    value: {
      type: Number,
      default: null,
    },
    /**
     * 単位
     */
    unit: {
      type: String,
      default: null,
    },
    /**
     * センサー値コード定義
     */
    valueType: {
      type: Array,
      default: null,
    },
    /**
     * 1remのサイズ(px)
     */
    remSize: {
      type: Number,
      default: 16,
    },
  },
  setup(props) {
    const { sensorItemName, value, unit, valueType } = toRefs(props)
    const { text, valueText, isDiscrete, canShowUnit } =
      useUserStatusAdditionalItemSensorData({
        sensorItemName,
        value,
        unit,
        valueType,
      })
    return { text, valueText, isDiscrete, canShowUnit }
  },
  data: function () {
    return {
      // 要素幅
      width: {
        root: 0,
        value: 0,
        name: 0,
        gap: 4,
        icon: 16, // アイコン12px + 右gap4px
      },
      // 表示字数閾値
      thresholdChars: {
        value: 7, // 7文字
        name: 5, // 4文字 + 両括弧0.5*2文字
      },
    }
  },
  computed: {
    /**
     * 文字サイズ(px)
     * @type {Number}
     */
    fontSize: function () {
      const ratio = +VARIABLES.FONT_SIZE_SMALL.replace('rem', '')
      return this.remSize * ratio
    },
    /**
     * 省略が必要な場合はtrue
     * @type {Object}
     */
    needsToTruncate: function () {
      const {
        root: wRoot,
        gap: wGap,
        name: wName,
        value: wValue,
        icon: wIcon,
      } = this.width

      // 全体から必ず除く分(アイコン,単位,単位の右gap)を引いた幅
      const wLeftRoot = wRoot - wIcon - wGap

      // 省略しない値テキスト幅 が 全体 から 想定される名称部 を除いた幅に収まらない場合は長すぎる
      const wMinName = Math.min(wName, this.thresholdChars.name * this.fontSize)
      const isValueTooLong = wValue > wLeftRoot - wMinName

      // 省略しない名称テキスト幅 が 全体 から 想定される値部 を除いた幅に収まらない場合は長過ぎる
      const wMinValue = Math.min(
        wValue,
        this.thresholdChars.value * this.fontSize
      )
      const isNameTooLong = wName > wLeftRoot - wMinValue

      return {
        value: isValueTooLong,
        name: isNameTooLong,
      }
    },
    /**
     * 値と名称のどちらかに省略が必要な場合はtrue
     * @type {boolean}
     */
    needsToTruncateSome: function () {
      return [...Object.values(this.needsToTruncate)].some((v) => v)
    },
    /**
     * 値と名称の両方に省略が必要な場合はtrue
     * @type {boolean}
     */
    needsToTruncateBoth: function () {
      return [...Object.values(this.needsToTruncate)].every((v) => v)
    },
    /**
     * アイコン部ツールチップテキスト
     * @type {string}
     */
    iconTooltipText: function () {
      return `${this.$t('appCardMemberItemSensorData.sensor')} : ${this.text}`
    },
    /**
     * ツールチップオプション
     * @type {Object}
     */
    tooltipOption: function () {
      return {
        title: this.text,
        disabled: !this.needsToTruncateSome,
      }
    },
  },
  watch: {
    valueText: function () {
      this.$nextTick(() => {
        this.$_measureWidth('value')
      })
    },
    sensorItemName: function () {
      this.$nextTick(() => {
        this.$_measureWidth('name')
      })
    },
    isDiscrete: function () {
      this.$nextTick(() => {
        this.$_measureWidth('value')
      })
    },
  },
  mounted: function () {
    this.observer = new ResizeObserver(() => this.$_measureWidth('root'))
    this.observer.observe(this.$el)
    this.$_measureWidth('value')
    this.$_measureWidth('name')
  },
  beforeDestroy: function () {
    this.observer.unobserve(this.$el)
  },
  methods: {
    /**
     * 要素幅を計測する
     * @param {string} key 対象要素のキー
     */
    $_measureWidth: function (key) {
      const target = key === 'root' ? this.$el : this.$refs[key]
      const rect = target?.getBoundingClientRect()
      this.width[key] = rect?.width || 0
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../assets/scss/parts/member/card-member-item';

.text-wrapper {
  display: inline-flex;
  justify-content: flex-start;
  flex-wrap: nowrap;
}

.value {
  --basis-chars: 7;

  display: grid;
  grid-template-columns: auto max-content;

  .text {
    margin: 0;
  }

  .unit {
    margin-left: 4px;
  }

  // 子に.ellipsisがあるときは単位との間にgapを入れない
  &:has(.ellipsis) {
    .unit {
      margin: 0;
    }
  }

  // 子に.ellipsisがないときは全文表示を保証する
  &:not(:has(.ellipsis)) {
    flex-shrink: 0;
  }

  // 値と名称の両方が省略されるときは
  // * 基本サイズとして--basis-chars文字分の幅を確保する
  // * 値:名称 が 2:1 でスペースを割り振られるようにする
  &:has(.ellipsis.both) {
    flex-grow: 2;
    flex-basis: calc(#{$font-size-small} * var(--basis-chars));
  }
}

.item-name {
  --basis-chars: 5;

  display: grid;
  grid-template-columns: max-content auto max-content;
  margin: 0 !important;

  .text {
    margin: 0;
  }
  &::before {
    content: '(';
  }
  &::after {
    content: ')';
  }

  // 子に.ellipsisがないときは全文表示を保証する
  &:not(:has(.ellipsis)) {
    flex-shrink: 0;
  }

  // 値と名称の両方が省略されるときは
  // * 基本サイズとして--basis-chars文字分の幅を確保する
  // * 値:名称 が 2:1 でスペースを割り振られるようにする
  &:has(.ellipsis.both) {
    flex-grow: 1;
    flex-basis: calc(#{$font-size-small} * var(--basis-chars));
  }
}
</style>
